/**
 * El copy de la entrevista (GKTIBO-70).
 *
 * Vive aparte de `textos.ts` porque son seis preguntas con su porqué y su
 * ejemplo —lo más largo del alta— y juntas empujaban ese archivo por encima de
 * las 300 líneas. Separarlo también deja claro qué se toca cuando se ajusta una
 * pregunta: esto, y nada más.
 */

/**
 * Las preguntas que ninguna página web contesta.
 *
 * La ingesta encuentra servicios, zonas, horarios y precios. **No
 * encuentra** qué NO hace el negocio, hasta dónde llega, cuándo hay que
 * llamar a una persona, ni en qué tono hablar. Son justo las que evitan que
 * el bot prometa lo que el negocio no puede cumplir.
 *
 * El ejemplo va como AYUDA debajo del campo, no como `placeholder` ni como
 * un botón que lo copia: un ejemplo que termina siendo la respuesta se
 * convierte en la verdad del negocio, y el bot lo dice como tal (G3, L27).
 */
export const entrevista = {
  titulo: 'Lo que nadie escribe',
  bajada: 'No están en ninguna web ni en ningún folleto, y son las que más importan.',
  cuenta: (actual: number, total: number) => `${actual} de ${total}`,
  responder: 'Responder',
  guardando: 'Guardando…',
  listoTitulo: 'Tu bot ya sabe lo que necesita',
  listoTexto: 'Sabe qué hacés, qué no, hasta dónde llegás y cuándo avisarte.',
  ejemplo: 'Por ejemplo:',
  vacia: 'Contanos aunque sea en una línea: es lo que evita que tu bot prometa de más.',
  noSePudo: 'No pudimos guardar tu respuesta. Probá de nuevo.',

  /**
   * La conversación (GKTIBO-88).
   *
   * Reemplaza al formulario de una pregunta por vez. La tarjeta de
   * confirmación es **el editor**: el dueño ve el texto exacto que va a
   * quedar, y nada llega al bot sin que lo apruebe. Es lo único que impide
   * que una invención del modelo pase en silencio.
   */
  chat: {
    saludo: 'Hola. Te voy a hacer unas preguntas cortas para que tu bot sepa de tu negocio.',
    /**
     * Cómo se llama cada tema en la pantalla del dueño.
     *
     * Sin esto la tarjeta de confirmación mostraba el id crudo —`NOHACE`— que
     * es nuestro nombre interno, no el suyo (D6).
     */
    nombreDelTema: {
      sobre: 'Sobre tu negocio',
      ofrece: 'Qué ofrecés',
      horarios: 'Cuándo atendés',
      noHace: 'Qué NO hacés',
      escalar: 'Cuándo pasar a una persona',
      extra: 'Lo que quieras agregar',
    } as Record<string, string>,
    // La franja que dice quién habla (GKTIBO-124). La misma caja sirve a dos
    // interlocutores, así que quién es NO puede vivir sólo en el color.
    hablaTibo: 'Tibo te está preguntando',
    hablaTiboBajada: 'Lo que no está en ninguna web',
    escribi: 'Escribí acá…',
    enviar: 'Enviar',
    pensando: 'Tibo está escribiendo…',
    // Lo que se va a guardar, antes de guardarlo.
    voyAGuardar: 'Esto le voy a enseñar a tu bot',
    guardar: 'Guardar',
    corregir: 'Corregir',
    guardado: 'Guardado. Tu bot ya lo sabe.',
    comoQuerésQueQuede: 'Contame cómo querés que quede y lo reescribo.',
    // El modelo agregó un dato que el dueño no dijo. Se avisa sin culparlo a
    // él ni explicar de más: lo que importa es que vea SU texto.
    sinInventos: 'Dejé tus palabras tal cual, para no ponerle nada que no dijiste.',
    // El modelo no se pudo usar. Se dice, y se dice que es nuestro: callarlo
    // hace que el dueño lo lea como un asistente tonto y se pelee con el
    // producto en vez de avisarnos (2026-09-21).
    sinAsistente:
      'No pude ordenarlo esta vez —es un problema nuestro, no tuyo—, así que guardo tus palabras tal cual. Se entiende igual.',
    noSePudoConversar: 'No pude procesar eso ahora. Probá de nuevo en un momento.',
    // Falló GUARDAR, no conversar. Decir «no pude procesar eso» ahí hace que
    // el dueño crea que el problema fue su respuesta, y la reescriba al pedo.
    noSePudoGuardar: 'No pude guardarlo. Tocá «Guardar» de nuevo en un momento.',
    noSePudoSaberCualSigue:
      'Se guardó, pero no pude ver qué sigue. Escribime cualquier cosa y sigo desde ahí.',
    terminado: 'Listo. Tu bot ya sabe lo que necesita para atender.',
    revisarALaIzquierda: 'Si algo no quedó como querías, tocá «Cambiar» en la lista y me lo decís.',
  },

  /**
   * El último turno: cómo suena (GKTIBO-122).
   *
   * Las preguntas de muestra viven acá, no en el servidor. Es la misma razón
   * por la que `aperturaDeConversacion` devuelve el id del tema y no la
   * pregunta: el copy en castellano vive en el panel (D6), y tenerlo en dos
   * lugares es tenerlo mal en uno de los dos.
   */
  voz: {
    pregunta: '¿Cómo querés que suene tu bot?',
    porque: 'Lo vas a poder escuchar acá abajo antes de guardarlo.',

    tieneNombre: '¿Tu bot tiene nombre?',
    sinNombre: (negocio: string) => `No, habla como ${negocio}`,
    conNombre: 'Sí, se llama…',
    etiquetaDelNombre: 'Nombre de tu bot',

    comoHabla: '¿Cómo le habla a tu gente?',
    oTocaUno: 'o tocá uno:',
    /**
     * Atajos, no la respuesta.
     *
     * Rellenan el campo y el dueño los edita. Un atajo mete palabras que él no
     * escribió —que es lo que L27 dice que no hagamos—, y se aceptan porque
     * son **etiquetas de estilo**: «Formal» no inventa un precio ni un horario.
     */
    atajos: [
      { id: 'cercano', label: 'Cercano', texto: 'Cercano y claro, sin tecnicismos.' },
      { id: 'formal', label: 'Formal', texto: 'Formal y preciso, sin confianzas.' },
      { id: 'directo', label: 'Directo', texto: 'Directo y breve, sin vueltas.' },
      { id: 'usted', label: 'De usted', texto: 'Trata de usted, con respeto y cordialidad.' },
    ],

    cuantoExplica: '¿Contesta corto o explica?',
    alGrano: 'Al grano',
    explicaUnPoco: 'Explica un poco',

    asiSuena: 'Así te va a sonar',
    escuchar: 'Escuchar cómo suena',
    otroEjemplo: 'Otro ejemplo',
    escuchando: 'Escuchando…',
    // Se dice que es nuestro. Callarlo hace que el dueño lo lea como un bot
    // roto y se pelee con el producto en vez de avisarnos.
    noSePudoEscuchar: 'No pudimos hacerlo hablar ahora —es un problema nuestro—. Probá de nuevo.',

    /** Lo que le preguntaría un cliente. Rota con «Otro ejemplo». */
    ejemplos: [
      '¿qué hacen ustedes?',
      '¿cuánto me sale?',
      'buenas, atienden los domingos?',
      'necesito que vengan hoy mismo',
    ],

    guardar: 'Guardar y probarlo',
    guardando: 'Guardando…',
    seCambia: 'Lo podés cambiar cuando quieras desde el panel.',
  },

  preguntas: {
    noHace: {
      q: '¿Hay algo que la gente te pida seguido y que NO hagas?',
      porque: 'Es lo que más evita que tu bot prometa algo que no podés cumplir.',
      ej: 'Urgencias de madrugada, animales de granja, atención a domicilio.',
    },
    limites: {
      q: '¿Hasta dónde llegás? Zonas, capacidad, lo que tenga un límite.',
      porque: 'Sin esto tu bot le dice que sí a cualquier barrio y después alguien viaja al pedo.',
      ej: 'Sólo el centro y el norte. A domicilio hasta veinte cuadras.',
    },
    escalar: {
      q: '¿Cuándo querés que deje de responder y te pase la conversación?',
      porque: 'Tu bot va a hacer exactamente esto, así que conviene ser concreto.',
      ej: 'Si es urgente, si el cliente está molesto, o si preguntan algo que no está acá.',
    },
    tono: {
      q: '¿Cómo querés que le hable a tu gente?',
      porque: 'Va a sonar así en cada mensaje.',
      ej: 'Cercano pero serio. Somos profesionales, no vendedores.',
    },
    ofrece: {
      q: '¿Qué ofrecés? Lo que te preguntan todo el tiempo.',
      porque: 'No lo encontramos en lo que nos diste.',
      ej: 'Qué hacés, cuánto sale, cuánto tarda.',
    },
    horarios: {
      q: '¿Cuándo atendés?',
      porque: 'No lo encontramos en lo que nos diste.',
      ej: 'Los días y las horas. Y si cerrás algún día, decilo.',
    },
  },
} as const;
